/**
 * Version History Viewer
 * Display all versions of a plan with timestamps
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VersionHistory = () => {
  const { planId } = useParams();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (planId) fetchVersions();
  }, [planId]);

  const fetchVersions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/plan-versions/plan/${planId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVersions(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
      setLoading(false);
    }
  };

  const setBaseline = async (versionId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/plan-versions/${versionId}/set-baseline`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchVersions();
      alert('Baseline set');
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  if (loading) return <div className="p-8">Loading versions...</div>;

  return (
    <div className="p-8">
      <h1 className="text-3xl font-bold mb-6">Version History</h1>

      <div className="space-y-3">
        {versions.map((v, idx) => (
          <div key={v.id} className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl font-bold text-blue-600">v{v.version_number}</span>
                  {v.is_baseline && (
                    <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full font-semibold">
                      ⭐ Baseline
                    </span>
                  )}
                  {v.is_submitted && (
                    <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-semibold">
                      📤 Submitted
                    </span>
                  )}
                  {idx === 0 && (
                    <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full font-semibold">
                      🆕 Latest
                    </span>
                  )}
                </div>
                
                <div className="text-sm text-gray-600 space-y-1">
                  <div>🕒 Created: {new Date(v.created_at).toLocaleString()}</div>
                  <div>👤 By: {v.created_by?.substring(0, 20)}...</div>
                  {v.comment && <div>💬 Comment: {v.comment}</div>}
                  {v.content_hash && (
                    <div className="text-xs">
                      🔒 Hash: {v.content_hash.substring(0, 16)}...
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex flex-col gap-2">
                {!v.is_baseline && (
                  <button
                    onClick={() => setBaseline(v.id)}
                    className="text-sm bg-purple-600 text-white px-3 py-1 rounded hover:bg-purple-700"
                  >
                    Set Baseline
                  </button>
                )}
                {idx < versions.length - 1 && (
                  <button className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700">
                    Compare →
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {versions.length === 0 && (
        <div className="text-center py-12 text-gray-500 bg-white rounded-lg">
          No versions found. Versions are created when plans are submitted.
        </div>
      )}
    </div>
  );
};

export default VersionHistory;